import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import AuthShell from '../../components/auth/AuthShell';
import { Eye, EyeOff } from 'lucide-react';
import Loader2 from '@/components/brand/Loader2';

// Map plan_id (from /register?plan=...) to the onboarding mode the user
// should land on. Drives Onboarding pre-selection so the user doesn't
// have to choose twice or re-register if they picked the wrong plan first.
const PLAN_ID_TO_MODE = {
  free_explore: 'health_individual',
  salud_individual: 'health_individual',
  salud_familiar: 'health_family',
  performance_individual: 'performance_individual',
  performance_team: 'performance_team',
  coach_basic: 'coach',
  coach_pro: 'coach',
  coach_elite: 'coach',
};

const Register = () => {
  const navigate = useNavigate();
  const { register, user, loading: authLoading } = useAuth();
  const [searchParams] = useSearchParams();
  const planFromUrl = searchParams.get('plan');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    acceptLegal: false,
  });

  // GUARDA: con sesión iniciada NUNCA se muestra el formulario de registro.
  // Quien llega aquí ya tiene cuenta y lo que quiere es cambiar de plan, así
  // que lo mandamos a Suscripción (con el plan elegido, si venía en la URL).
  // `submittedRef` evita que esta guarda interfiera con el flujo normal justo
  // después de registrarse (cuando `user` pasa a existir).
  const submittedRef = useRef(false);
  useEffect(() => {
    if (authLoading || submittedRef.current || !user) return;
    navigate(planFromUrl ? `/app/billing?upgrade=${planFromUrl}` : '/app/billing', { replace: true });
  }, [authLoading, user, planFromUrl, navigate]);

  // Persist the plan choice so it survives the OAuth-like
  // register→login→onboarding bounce without depending on the URL.
  useEffect(() => {
    if (planFromUrl) {
      const mode = PLAN_ID_TO_MODE[planFromUrl];
      sessionStorage.setItem('onor_intended_plan', planFromUrl);
      if (mode) sessionStorage.setItem('onor_intended_mode', mode);
    }
  }, [planFromUrl]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    submittedRef.current = true;
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    if (!formData.acceptLegal) {
      toast.error('Para crear tu cuenta debes aceptar los Términos y Condiciones y el Aviso de Privacidad');
      return;
    }

    setLoading(true);

    try {
      await register(formData.email, formData.password, formData.name, formData.phone, formData.acceptLegal);
      toast.success('¡Cuenta creada exitosamente!');
      navigate('/onboarding');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear cuenta');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell>
      <div className="w-full">
        <div className="w-full">
          <h1 className="font-display font-bold uppercase text-3xl leading-9 text-center text-zinc-100 onor-rise">
            Crear cuenta
            <small className="block text-sm leading-5 font-medium tracking-wider normal-case text-zinc-400 mt-1">
              ¿Ya tienes cuenta?{' '}
              <Link to="/login" className="text-bronze underline underline-offset-4 hover:text-zinc-100">Inicia sesión</Link>
            </small>
          </h1>

          {/* Visual confirmation that the plan choice survived the click */}
          {planFromUrl && (
            <div className="mb-6 p-3 rounded-sm border border-primary/30 bg-primary/5 text-sm text-primary flex items-center justify-between gap-2">
              <span>
                Plan seleccionado:{' '}
                <span className="font-medium text-white">
                  {{
                    free_explore: 'Explorar Gratis',
                    salud_individual: 'Salud Individual',
                    salud_familiar: 'Salud Familiar',
                    performance_individual: 'Rendimiento Individual',
                    performance_team: 'Rendimiento Equipo',
                    coach_basic: 'Coach Básico',
                    coach_pro: 'Coach Pro',
                    coach_elite: 'Coach Élite',
                  }[planFromUrl] || planFromUrl}
                </span>
              </span>
              <Link to="/planes" className="text-xs underline text-primary hover:text-primary">
                Cambiar
              </Link>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-zinc-300">Nombre completo</Label>
              <Input
                id="name"
                type="text"
                placeholder="Tu nombre"
                className="input-default"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                data-testid="register-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-zinc-300">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="tu@email.com"
                className="input-default"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                data-testid="register-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone" className="text-zinc-300">Teléfono (opcional)</Label>
              <Input
                id="phone"
                type="tel"
                placeholder="+52 123 456 7890"
                className="input-default"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                data-testid="register-phone-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-zinc-300">Contraseña</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Mínimo 6 caracteres"
                  className="input-default pr-10"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                  aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-zinc-300">Confirmar contraseña</Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="Repite tu contraseña"
                className="input-default"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                required
                data-testid="register-confirm-password-input"
              />
            </div>

            <label className="flex items-start gap-3 cursor-pointer" data-testid="register-legal">
              <Checkbox
                className="mt-0.5"
                checked={formData.acceptLegal}
                onCheckedChange={(checked) => setFormData({ ...formData, acceptLegal: checked === true })}
                data-testid="register-legal-checkbox"
              />
              <span className="text-zinc-300 text-xs leading-relaxed font-manrope">
                Leí y acepto los{' '}
                <a href="/terminos" target="_blank" rel="noopener noreferrer" className="text-primary underline">
                  Términos y Condiciones
                </a>{' '}
                y el{' '}
                <a href="/privacidad" target="_blank" rel="noopener noreferrer" className="text-primary underline">
                  Aviso de Privacidad
                </a>
                , y otorgo mi consentimiento expreso para el tratamiento de mis datos personales,
                incluidos los de salud, para elaborar mis planes.
              </span>
            </label>

            <Button 
              type="submit" 
              className="btn-primary w-full" 
              disabled={loading || !formData.acceptLegal}
              data-testid="register-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  CREANDO CUENTA...
                </>
              ) : (
                'CREAR MI CUENTA'
              )}
            </Button>
          </form>
        </div>
      </div>
    </AuthShell>
  );
};

export default Register;
